import { useAuth } from '@/contexts/AuthContext'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import Announcements from '@/components/Announcements'
import LatestThreads from '@/components/LatestThreads'
import ThreadsFromMyGroups from '@/components/ThreadsFromMyGroups'

export default function Home() {
  const { user } = useAuth()

  const { data: profile } = useQuery({
    queryKey: ['profile-home', user?.id],
    queryFn: async () => {
      if (!user) return null
      const { data, error } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user.id)
        .maybeSingle()
      if (error) {
        console.warn('Error fetching profile:', error)
        return null
      }
      return data
    },
    enabled: !!user,
    staleTime: 60000,
  })

  const displayName = profile?.display_name || user?.email || 'User'

  if (!user) {
    return (
      <div className="max-w-6xl mx-auto">
        <h1
          className="text-3xl font-bold mb-6"
          style={{ color: 'var(--text-primary)' }}
        >
          Welcome to InsideAccess
        </h1>
        <p
          className="text-lg mb-8"
          style={{ color: 'var(--text-primary)' }}
        >
          An accessible community platform for audio description, where blind and partially sighted users
          can ask questions, share knowledge, and discover information about audio-described content.
        </p>
        <div className="flex gap-4">
          <Link
            to="/register"
            className="px-6 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
          >
            Get Started
          </Link>
          <Link
            to="/login"
            className="px-6 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
            }}
          >
            Login
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto">
      <h1
        className="text-3xl font-bold mb-2"
        style={{ color: 'var(--text-primary)' }}
      >
        Welcome, {displayName}
      </h1>
      <p
        className="text-lg mb-8"
        style={{ color: 'var(--text-primary)' }}
      >
        Choose where to go next.
      </p>

      <div className="space-y-8">
        {/* Announcements */}
        <section aria-labelledby="home-announcements">
          <Announcements />
        </section>

        {/* TV Listings */}
        <section
          className="rounded-lg p-6 border-2"
          style={{
            backgroundColor: 'var(--bg-primary)',
            borderColor: 'var(--bg-tertiary)',
          }}
          aria-labelledby="home-tv-listings"
        >
          <h2
            id="home-tv-listings"
            className="text-xl font-bold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            TV Listings
          </h2>
          <p
            className="text-sm mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            View today&apos;s TV schedule
          </p>
          <Link
            to="/tv-guide"
            className="inline-block px-4 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
          >
            Open TV Guide
          </Link>
        </section>

        {/* Streaming */}
        <section
          className="rounded-lg p-6 border-2"
          style={{
            backgroundColor: 'var(--bg-primary)',
            borderColor: 'var(--bg-tertiary)',
          }}
          aria-labelledby="home-streaming"
        >
          <h2
            id="home-streaming"
            className="text-xl font-bold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            Streaming
          </h2>
          <p
            className="text-sm mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            Discover where to watch titles with accessibility options
          </p>
          <Link
            to="/streaming"
            className="inline-block px-4 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
          >
            Open Streaming
          </Link>
        </section>

        {/* 2. Latest Threads */}
        <section aria-labelledby="home-latest-threads">
          <LatestThreads userId={user.id} defaultExpanded />
        </section>

        {/* 3. Threads from My Groups */}
        <section aria-labelledby="home-threads-from-my-groups">
          <ThreadsFromMyGroups userId={user.id} />
        </section>

        {/* 4. My Groups + My Links — entry cards */}
        <div className="space-y-6">
          <Link to="/groups" className="block">
            <div
              className="rounded-lg p-6 border-2"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--bg-interactive)',
              }}
            >
              <h2
                className="text-xl font-bold mb-2"
                style={{ color: 'var(--text-primary)' }}
              >
                Groups
              </h2>
              <p
                className="text-sm underline"
                style={{ color: 'var(--text-primary)' }}
              >
                View and manage your groups.
              </p>
            </div>
          </Link>
          <Link to="/links" className="block">
            <div
              className="rounded-lg p-6 border-2"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--bg-interactive)',
              }}
            >
              <h2
                className="text-xl font-bold mb-2"
                style={{ color: 'var(--text-primary)' }}
              >
                Links
              </h2>
              <p
                className="text-sm underline"
                style={{ color: 'var(--text-primary)' }}
              >
                Your saved links and resources.
              </p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  )
}
